import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

/* ============================================================
   小元件
   ============================================================ */

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
          <i className="ri-sparkling-line text-primary-700" />
        </div>
        <span className="text-xs font-semibold text-accent-600 tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-foreground-900">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-foreground-600">{subtitle}</p>}
      <div className="w-12 h-1 bg-accent-500 mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : `scroll-reveal-delay-${delay}`;
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

/* 動態數字計數器 */
function CountUp({ end, suffix = '', duration = 2000 }: { end: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let start = 0;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      start = Math.floor(eased * end);
      setCount(start);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* 背景粒子效果 */
function FloatingParticles() {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    left: `${8 + (i * 7) % 84}%`,
    top: `${10 + (i * 13) % 70}%`,
    size: 4 + (i % 3) * 3,
    delay: i * 0.7,
    duration: 4 + (i % 3) * 2,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full animate-float"
          style={{
            left: p.left,
            top: p.top,
            width: p.size,
            height: p.size,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            backgroundColor: p.id % 3 === 0 ? 'rgba(255,255,255,0.22)' : p.id % 3 === 1 ? 'rgba(212,165,116,0.18)' : 'rgba(46,139,87,0.15)',
          }}
        />
      ))}
    </div>
  );
}

/* ============================================================
   主頁面
   ============================================================ */

export default function AssociationPage() {
  const navigate = useNavigate();

  const lifestyleItems = [
    {
      icon: 'ri-heart-pulse-line',
      title: '慢性病管理',
      desc: '糖尿病、高血壓等慢性病與聽力損失密切相關。我們整合慢病管理與聽力保健，提供跨科會診與追蹤服務。',
      image: 'https://readdy.ai/api/search-image?query=elderly%20person%20with%20chronic%20disease%20management%20and%20hearing%20care%20consultation%20in%20modern%20clinic%20warm%20lighting%20green%20tones%20professional%20healthcare%20photography%20wide%20angle&width=600&height=400&seq=70&orientation=landscape',
    },
    {
      icon: 'ri-footprint-line',
      title: '共聽長者戶外活動',
      desc: '定期舉辦聽損長者戶外踏青、園藝療癒、輕旅行等活動，讓聽損者走出家門，享受社交與自然的美好。',
      image: 'https://readdy.ai/api/search-image?query=group%20of%20elderly%20people%20with%20hearing%20aids%20enjoying%20outdoor%20garden%20activity%20warm%20sunny%20day%20green%20park%20environment%20smiling%20happy%20social%20gathering%20photography&width=600&height=400&seq=71&orientation=landscape',
    },
    {
      icon: 'ri-restaurant-line',
      title: '共聽友善餐敘',
      desc: '與友善餐廳合作，打造噪音適中、座位安排利於聽損者溝通的用餐環境，讓聚餐不再有聽不清楚的困擾。',
      image: 'https://readdy.ai/api/search-image?query=elderly%20friends%20dining%20together%20in%20warm%20quiet%20restaurant%20with%20good%20acoustics%20comfortable%20seating%20soft%20lighting%20happy%20conversation%20photography&width=600&height=400&seq=72&orientation=landscape',
    },
    {
      icon: 'ri-run-line',
      title: '運動健身整合',
      desc: '配合運動健身中心，提供適合聽損者的運動課程，如太極、瑜珈、水中運動，促進身心健康。',
      image: 'https://readdy.ai/api/search-image?query=elderly%20people%20doing%20tai%20chi%20exercise%20in%20park%20green%20nature%20morning%20light%20peaceful%20healthy%20lifestyle%20photography%20wide%20angle&width=600&height=400&seq=73&orientation=landscape',
    },
  ];

  const clinics = [
    { name: '富聆聽聽力中心', type: '聽力專科', location: '台北・大安', partners: '8 家門市' },
    { name: '仁心耳鼻喉科', type: '耳鼻喉科', location: '台北・信義', partners: '3 位醫師' },
    { name: '安康家醫診所', type: '家庭醫學科', location: '新北・板橋', partners: '慢病管理' },
    { name: '樂齡健康中心', type: '復健科', location: '台中・西區', partners: '聽語治療' },
    { name: '長庚聽力中心', type: '聽力專科', location: '桃園・桃園', partners: '早療合作' },
    { name: '和信耳鼻喉科', type: '耳鼻喉科', location: '高雄・前鎮', partners: '耳鳴治療' },
  ];

  const chronicConditions = [
    {
      icon: 'ri-drop-line',
      title: '糖尿病與聽損',
      desc: '研究顯示糖尿病患者發生聽損的風險是常人的 2 倍以上。高血糖會損傷內耳血管與神經，導致漸進性聽力下降。',
    },
    {
      icon: 'ri-heart-3-line',
      title: '心血管疾病與聽損',
      desc: '高血壓、動脈硬化會影響內耳血液供應，加速聽力退化。聽損也可能是心血管疾病的早期徵兆。',
    },
    {
      icon: 'ri-brain-line',
      title: '認知退化與聽損',
      desc: '聽力損失會增加認知退化的風險。早期配戴助聽器可減緩認知退化速度，維持大腦活力。',
    },
    {
      icon: 'ri-mental-health-line',
      title: '心理健康與聽損',
      desc: '聽損者常因溝通困難而社交退縮，進而引發孤獨、憂鬱等心理問題。我們提供全方位的身心健康支持。',
    },
  ];

  const csrActivities = [
    {
      year: '2025',
      title: '聲音平權校園巡迴',
      desc: '走進 32 所國中小學，透過互動體驗讓學生感受聽損者的生活，培養同理心與聽力保健意識。',
      icon: 'ri-school-line',
    },
    {
      year: '2025',
      title: '職場聽力友善認證',
      desc: '協助企業建立聽損友善職場環境，提供輔助溝通設備建議與員工聽力教育訓練。已有 15 家企業獲得認證。',
      icon: 'ri-building-line',
    },
    {
      year: '2024',
      title: '偏鄉聽力義診',
      desc: '深入花東、離島等偏鄉地區，為行動不便的長者提供免費聽力檢測與助聽器清潔保養服務。',
      icon: 'ri-hospital-line',
    },
    {
      year: '2024',
      title: '無聲咖啡師計畫',
      desc: '培訓聽損青年成為專業咖啡師，透過手語與視覺化溝通，在咖啡館創造共融就業環境。',
      icon: 'ri-cup-line',
    },
  ];

  const communityStats = [
    { num: 12000, suffix: '+', label: '協會會員', icon: 'ri-user-smile-line' },
    { num: 50, suffix: '+', label: '合作機構', icon: 'ri-hospital-line' },
    { num: 200, suffix: '+', label: '年度活動', icon: 'ri-calendar-event-line' },
    { num: 8, suffix: '', label: '亞洲國家', icon: 'ri-global-line' },
  ];

  const asiaCountries = [
    { name: '台灣', role: '發起國', flag: 'ri-map-pin-line', desc: '協會總部所在地，推動各項聽力平權與健康整合計畫' },
    { name: '日本', role: '技術合作', flag: 'ri-map-pin-line', desc: '引進先進聽力檢測技術與助聽器研發經驗交流' },
    { name: '韓國', role: '論壇合辦', flag: 'ri-map-pin-line', desc: '共同舉辦亞洲聽力健康論壇，促進區域合作' },
    { name: '新加坡', role: '服務模式', flag: 'ri-map-pin-line', desc: '學習多元文化下的聽力服務模式與社區營造' },
    { name: '馬來西亞', role: '資源串接', flag: 'ri-map-pin-line', desc: '東南亞聽力資源串接，推動跨境聽力教育計畫' },
    { name: '泰國', role: '產品研發', flag: 'ri-map-pin-line', desc: '合作開發適合熱帶氣候的助聽器保養方案' },
    { name: '越南', role: '義診培訓', flag: 'ri-map-pin-line', desc: '偏鄉聽力義診與基層聽力師培訓計畫' },
    { name: '菲律賓', role: '兒童權益', flag: 'ri-map-pin-line', desc: '共同倡議聽損兒童早期療育與教育權益' },
  ];

  const asiaVisionPoints = [
    {
      icon: 'ri-earth-line',
      title: '區域標準制定',
      desc: '推動亞洲聽力篩檢標準化，建立跨國數據比對機制，讓各國聽力政策有科學依據。',
    },
    {
      icon: 'ri-exchange-line',
      title: '跨境資源共享',
      desc: '串接 8 國聽力機構資料庫，共享研究文獻、臨床案例與教育資源，降低重複投入。',
    },
    {
      icon: 'ri-award-line',
      title: '人才培訓認證',
      desc: '建立亞洲聽力師共通認證制度，促進專業人才跨國流動與技術交流。',
    },
    {
      icon: 'ri-lightbulb-line',
      title: '創新技術研發',
      desc: '聯合各國研發機構，針對亞洲人耳型與環境特性開發專屬助聽技術與解決方案。',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ============================================================
            ===== HERO 區塊 — 強化亞洲佈局氣勢 =====
            ============================================================ */}
        <div className="relative overflow-hidden text-white pt-4 md:pt-6 pb-8 md:pb-12">
          {/* 深色基底 — 從最深到深，層層堆疊 */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-primary-900 to-primary-950" />
          {/* 上方光暈 — 琥珀色，點亮頂部 */}
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] md:w-[700px] md:h-[700px] rounded-full bg-accent-500/20 blur-[140px]" />
          {/* 下方光暈 — 綠色光池 */}
          <div className="absolute -bottom-28 -left-16 w-[400px] h-[400px] md:w-[550px] md:h-[550px] rounded-full bg-primary-600/25 blur-[120px]" />
          {/* 中央亮點 — 小面積高光 */}
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 md:w-72 md:h-72 rounded-full bg-accent-400/12 blur-[100px]" />
          {/* 角落點綴 */}
          <div className="absolute top-16 left-12 w-24 h-24 md:w-36 md:h-36 rounded-full bg-primary-500/20 blur-3xl" />
          <div className="absolute bottom-20 right-16 w-32 h-32 md:w-44 md:h-44 rounded-full bg-accent-600/15 blur-3xl" />

          {/* 網格紋理 — 增加層次感 */}
          <div className="absolute inset-0 opacity-[0.025]">
            <svg width="100%" height="100%">
              <defs>
                <pattern id="hero-grid" x="0" y="0" width="48" height="48" patternUnits="userSpaceOnUse">
                  <circle cx="24" cy="24" r="0.8" fill="white" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="100%" height="100%" fill="url(#hero-grid)" />
            </svg>
          </div>

          <FloatingParticles />

          {/* 音波律動裝飾 */}
          <div className="absolute inset-0 opacity-[0.1] pointer-events-none">
            <svg viewBox="0 0 1440 500" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" fill="none">
              {/* 第一層 — 粗主波，低頻大振幅 */}
              <path
                d="M0 250 Q25 215 50 225 Q75 235 100 200 Q125 165 150 190 Q175 215 200 180 Q225 145 250 170 Q275 195 300 160 Q325 125 350 155 Q375 185 400 145 Q425 105 450 140 Q475 175 500 130 Q525 85 550 125 Q575 165 600 115 Q625 65 650 110 Q675 155 700 100 Q725 45 750 95 Q775 145 800 85 Q825 25 850 80 Q875 135 900 70 Q925 5 950 65 Q975 125 1000 55 Q1025 -15 1050 50 Q1075 115 1100 45 Q1125 -25 1150 40 Q1175 105 1200 35 Q1225 -35 1250 32 Q1275 99 1300 28 Q1325 -43 1350 25 Q1375 93 1400 22 Q1425 -49 1440 20"
                stroke="white"
                strokeWidth="2.8"
                strokeOpacity="0.65"
              />
              {/* 第二層 — 中頻波，節奏起伏 */}
              <path
                d="M0 300 Q20 280 40 290 Q60 300 80 275 Q100 250 120 268 Q140 286 160 260 Q180 234 200 255 Q220 276 240 248 Q260 220 280 242 Q300 264 320 235 Q340 206 360 230 Q380 254 400 225 Q420 196 440 220 Q460 244 480 215 Q500 186 520 210 Q540 234 560 205 Q580 176 600 200 Q620 224 640 195 Q660 166 680 190 Q700 214 720 185 Q740 156 760 180 Q780 204 800 175 Q820 146 840 170 Q860 194 880 165 Q900 136 920 160 Q940 184 960 155 Q980 126 1000 150 Q1020 174 1040 145 Q1060 116 1080 140 Q1100 164 1120 135 Q1140 106 1160 130 Q1180 154 1200 125 Q1220 96 1240 120 Q1260 144 1280 115 Q1300 86 1320 110 Q1340 134 1360 105 Q1380 76 1400 100 Q1420 124 1440 95"
                stroke="white"
                strokeWidth="1.5"
                strokeOpacity="0.4"
                strokeDasharray="12 6"
              />
              {/* 第三層 — 高頻細鋸齒 */}
              <path
                d="M0 350 L15 338 L30 348 L45 335 L60 347 L75 332 L90 344 L105 329 L120 341 L135 326 L150 339 L165 323 L180 336 L195 320 L210 333 L225 317 L240 330 L255 314 L270 327 L285 311 L300 324 L315 308 L330 321 L345 305 L360 318 L375 302 L390 315 L405 299 L420 312 L435 296 L450 309 L465 293 L480 306 L495 290 L510 303 L525 287 L540 300 L555 284 L570 297 L585 281 L600 294 L615 278 L630 291 L645 275 L660 288 L675 272 L690 285 L705 269 L720 282 L735 266 L750 279 L765 263 L780 276 L795 260 L810 273 L825 257 L840 270 L855 254 L870 267 L885 251 L900 264 L915 248 L930 261 L945 245 L960 258 L975 242 L990 255 L1005 239 L1020 252 L1035 236 L1050 249 L1065 233 L1080 246 L1095 230 L1110 243 L1125 227 L1140 240 L1155 224 L1170 237 L1185 221 L1200 234 L1215 218 L1230 231 L1245 215 L1260 228 L1275 212 L1290 225 L1305 209 L1320 222 L1335 206 L1350 219 L1365 203 L1380 216 L1395 200 L1410 213 L1425 197 L1440 210"
                stroke="white"
                strokeWidth="0.7"
                strokeOpacity="0.3"
              />
              {/* 等化器豎條 */}
              {Array.from({ length: 18 }, (_, i) => {
                const heights = [70, 110, 50, 90, 130, 60, 100, 80, 120, 55, 95, 75, 105, 45, 85, 115, 65, 100];
                const x = 40 + i * 80;
                const h = heights[i];
                return (
                  <rect
                    key={`bar-${i}`}
                    x={x}
                    y={420 - h}
                    width="3"
                    height={h}
                    rx="1.5"
                    fill="white"
                    opacity={0.25 + (i % 3) * 0.08}
                  />
                );
              })}
            </svg>
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto text-center pt-10 md:pt-14 pb-8 md:pb-10">
            <span className="inline-block text-xs font-semibold tracking-[0.25em] text-accent-300 uppercase mb-4 md:mb-5">
              Co-Hearing Life Association
            </span>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
              共聽生活<span className="text-accent-300">協會</span>
            </h1>
            <p className="mt-3 md:mt-4 text-base md:text-xl font-medium text-accent-300">
              從台灣出發，佈局全亞洲
            </p>
            <p className="mt-4 md:mt-5 text-white/75 text-sm md:text-base max-w-3xl mx-auto leading-relaxed">
              連結醫療、社區與產業，打造聽損友善的共融社會。
              我們以台灣為基地，攜手日本、韓國、新加坡等 8 個亞洲國家，
              建立區域聽力健康合作網絡，讓聽力平權的種子在亞洲生根。
            </p>

            {/* 亞洲國家標籤 */}
            <div className="mt-5 md:mt-6 flex flex-wrap items-center justify-center gap-2 md:gap-3">
              {['台灣', '日本', '韓國', '新加坡', '馬來西亞', '泰國', '越南', '菲律賓'].map((c, i) => (
                <span
                  key={c}
                  className="px-3.5 py-1.5 rounded-full text-xs font-medium bg-white/8 text-white/85 border border-white/15 backdrop-blur-sm hover:bg-white/14 hover:border-white/25 transition-all duration-300 cursor-default"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 120" fill="none" preserveAspectRatio="none">
            <path d="M0 120L60 108C120 96 240 72 360 60C480 48 600 48 720 54C840 60 960 72 1080 78C1200 84 1320 84 1380 84L1440 84V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
          </svg>
        </div>

        {/* ============================================================
            ===== 數據統計 Bar =====
            ============================================================ */}
        <div className="relative z-10 -mt-16 md:-mt-20 px-6 md:px-10 lg:px-16">
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-2xl border border-background-200 overflow-hidden">
              <div className="grid grid-cols-2 md:grid-cols-4">
                {communityStats.map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`flex items-center gap-3 md:gap-4 p-5 md:p-7 ${idx !== 0 ? 'border-t md:border-t-0 md:border-l border-background-200' : ''}`}
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
                      <i className={`${stat.icon} text-lg md:text-xl text-primary-700`} />
                    </div>
                    <div>
                      <div className="text-xl md:text-2xl font-bold text-foreground-900">
                        <CountUp end={stat.num} suffix={stat.suffix} />
                      </div>
                      <div className="text-sm text-foreground-600">{stat.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================
            ===== 亞洲戰略願景 — 全新區塊，凸顯佈局決心 =====
            ============================================================ */}
        <div className="px-6 md:px-10 lg:px-16 py-12 md:py-16 max-w-7xl mx-auto">
          <SectionHeader
            label="Asia Vision 2030"
            title="亞洲聽力健康戰略願景"
            subtitle="以台灣為起點，逐步構建覆蓋東亞與東南亞的聽力健康合作網絡"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* 左側：願景卡片 */}
            <SectionReveal>
              <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 h-full">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className="ri-compass-3-line text-2xl text-primary-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground-900">願景宣言</h3>
                    <p className="text-xs text-foreground-500">Vision Statement</p>
                  </div>
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                  共聽生活協會立志成為亞洲聽力健康領域的領航者。我們深信，聽力平權不應受國界限制——
                  從台北到東京、從首爾到新加坡，每一位聽損者都值得獲得同等品質的照護與尊重。
                </p>
                <div className="space-y-3">
                  {['2025 建立亞洲 8 國聽力機構合作網絡', '2026 推出亞洲聽力師共通認證制度', '2027 舉辦首屆亞洲聽力健康峰會', '2028 發表亞洲聽力健康白皮書', '2030 達成 12 國聯盟、影響 50 萬聽損者'].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-accent-600" />
                      </div>
                      <span className="text-sm text-foreground-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </SectionReveal>

            {/* 右側：四大戰略支柱 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {asiaVisionPoints.map((point, idx) => (
                <SectionReveal key={point.title} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-background-200 p-5 md:p-6 hover:border-accent-300 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                      <i className={`${point.icon} text-xl text-primary-700`} />
                    </div>
                    <h3 className="text-base font-bold text-foreground-900">{point.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{point.desc}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 多元健康生活型態整合 =====
            ============================================================ */}
        <div className="bg-background-100 py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader label="Holistic Lifestyle" title="多元健康生活型態整合" subtitle="從慢性病管理到戶外活動，我們整合各項健康資源，打造聽損友善的完整生活圈" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {lifestyleItems.map((item, idx) => (
                <SectionReveal key={item.title} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-background-200 overflow-hidden hover:border-accent-300 hover:shadow-lg transition-all duration-300 h-full flex flex-col">
                    <div className="w-full h-48 md:h-52 overflow-hidden relative">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-primary-900/0 group-hover:bg-primary-900/10 transition-colors duration-300" />
                      <div className="absolute top-4 left-4">
                        <div className="w-10 h-10 rounded-full bg-primary-800/90 flex items-center justify-center">
                          <i className={`${item.icon} text-white text-base`} />
                        </div>
                      </div>
                    </div>
                    <div className="p-5 md:p-6 flex-1 flex flex-col">
                      <h3 className="text-lg font-bold text-foreground-900">{item.title}</h3>
                      <p className="mt-2 text-sm text-foreground-600 leading-relaxed flex-1">{item.desc}</p>
                      <div className="mt-4 flex items-center gap-1 text-sm text-accent-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        了解更多
                        <i className="ri-arrow-right-line text-sm transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 診所聯盟 =====
            ============================================================ */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Clinic Alliance" title="診所聯盟" subtitle="串連全台各專科診所，建立聽力健康轉介網絡，提供跨科整合照護" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {clinics.map((clinic, idx) => (
              <SectionReveal key={clinic.name} delay={idx}>
                <div className="group bg-white rounded-2xl border border-background-200 p-5 md:p-6 hover:border-accent-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0 group-hover:bg-primary-200 transition-colors duration-300">
                      <i className="ri-hospital-line text-xl text-primary-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold text-foreground-900">{clinic.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-accent-700 font-medium bg-accent-100 px-2 py-0.5 rounded-full">{clinic.type}</span>
                        <span className="text-xs text-foreground-500">{clinic.location}</span>
                      </div>
                      <p className="mt-2 text-sm text-foreground-600">{clinic.partners}</p>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>

          <SectionReveal className="mt-8 md:mt-10">
            <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8 flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <i className="ri-team-line text-2xl text-primary-700" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-bold text-foreground-900">加入診所聯盟</h3>
                <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
                  歡迎各醫療院所、聽力中心加入共聽生活協會聯盟，共同建立聽力友善的醫療轉介網絡，為聽損者提供更完整的照護服務。
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="border border-background-200 text-foreground-600 px-6 py-3 rounded-xl text-sm font-semibold hover:border-accent-400 hover:text-foreground-900 transition-all duration-200 whitespace-nowrap flex items-center gap-2 shrink-0"
              >
                <i className="ri-mail-line" />
                聯繫我們
              </button>
            </div>
          </SectionReveal>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 聽損醫學與慢性共病 =====
            ============================================================ */}
        <div className="bg-background-100 py-10 md:py-14 relative overflow-hidden">
          {/* 音律波形裝飾 */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.07]">
            <svg viewBox="0 0 1440 360" className="absolute inset-0 w-full h-full" preserveAspectRatio="none" fill="none">
              {/* 第一層 — 粗線，低頻律動 */}
              <path
                d="M0 180 Q20 140 40 145 Q60 150 80 120 Q100 90 120 100 Q140 110 160 80 Q180 50 200 65 Q220 80 240 50 Q260 20 280 45 Q300 70 320 40 Q340 10 360 30 Q380 50 400 25 Q420 0 440 20 Q460 40 480 60 Q500 80 520 50 Q540 20 560 40 Q580 60 600 35 Q620 10 640 30 Q660 50 680 25 Q700 0 720 20 Q740 40 760 55 Q780 70 800 45 Q820 20 840 40 Q860 60 880 35 Q900 10 920 30 Q940 50 960 25 Q980 0 1000 20 Q1020 40 1040 55 Q1060 70 1080 45 Q1100 20 1120 40 Q1140 60 1160 80 Q1180 100 1200 75 Q1220 50 1240 65 Q1260 80 1280 55 Q1300 30 1320 50 Q1340 70 1360 85 Q1380 100 1400 80 Q1420 60 1440 75"
                stroke="currentColor"
                strokeWidth="3"
                className="text-foreground-800"
                opacity="0.6"
              />
              {/* 第二層 — 中線，中頻律動 */}
              <path
                d="M0 200 Q30 180 60 190 Q90 200 120 175 Q150 150 180 165 Q210 180 240 155 Q270 130 300 148 Q330 166 360 143 Q390 120 420 138 Q450 156 480 170 Q510 184 540 162 Q570 140 600 155 Q630 170 660 148 Q690 126 720 140 Q750 154 780 168 Q810 182 840 160 Q870 138 900 152 Q930 166 960 145 Q990 124 1020 140 Q1050 156 1080 135 Q1110 114 1140 130 Q1170 146 1200 158 Q1230 170 1260 150 Q1290 130 1320 144 Q1350 158 1380 142 Q1410 126 1440 138"
                stroke="currentColor"
                strokeWidth="2"
                className="text-accent-600"
                opacity="0.5"
              />
              {/* 第三層 — 細線，高頻律動 */}
              <path
                d="M0 220 Q25 215 50 222 Q75 229 100 218 Q125 207 150 215 Q175 223 200 210 Q225 197 250 206 Q275 215 300 202 Q325 189 350 199 Q375 209 400 196 Q425 183 450 194 Q475 205 500 192 Q525 179 550 190 Q575 201 600 188 Q625 175 650 186 Q675 197 700 183 Q725 169 750 181 Q775 193 800 180 Q825 167 850 179 Q875 191 900 178 Q925 165 950 176 Q975 187 1000 174 Q1025 161 1050 172 Q1075 183 1100 170 Q1125 157 1150 168 Q1175 179 1200 166 Q1225 153 1250 164 Q1275 175 1300 162 Q1325 149 1350 160 Q1375 171 1400 158 Q1425 145 1440 155"
                stroke="currentColor"
                strokeWidth="1.5"
                className="text-primary-600"
                opacity="0.5"
                strokeDasharray="8 4"
              />
              {/* 第四層 — 超細鋸齒線 */}
              <path
                d="M0 240 L15 232 L30 244 L45 228 L60 242 L75 226 L90 240 L105 224 L120 238 L135 222 L150 236 L165 220 L180 234 L195 218 L210 232 L225 216 L240 230 L255 214 L270 228 L285 212 L300 226 L315 210 L330 224 L345 208 L360 222 L375 206 L390 220 L405 204 L420 218 L435 202 L450 216 L465 200 L480 214 L495 198 L510 212 L525 196 L540 210 L555 194 L570 208 L585 192 L600 206 L615 190 L630 204 L645 188 L660 202 L675 186 L690 200 L705 184 L720 198 L735 182 L750 196 L765 180 L780 194 L795 178 L810 192 L825 176 L840 190 L855 174 L870 188 L885 172 L900 186 L915 170 L930 184 L945 168 L960 182 L975 166 L990 180 L1005 164 L1020 178 L1035 162 L1050 176 L1065 160 L1080 174 L1095 158 L1110 172 L1125 156 L1140 170 L1155 154 L1170 168 L1185 152 L1200 166 L1215 150 L1230 164 L1245 148 L1260 162 L1275 146 L1290 160 L1305 144 L1320 158 L1335 142 L1350 156 L1365 140 L1380 154 L1395 138 L1410 152 L1425 136 L1440 148"
                stroke="currentColor"
                strokeWidth="0.8"
                className="text-foreground-700"
                opacity="0.3"
              />
            </svg>
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader label="Hearing & Chronic Disease" title="聽損醫學與慢性共病" subtitle="聽力損失與多種慢性疾病息息相關，及早發現、整合管理是關鍵" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {chronicConditions.map((item, idx) => (
                <SectionReveal key={item.title} delay={idx}>
                  <div className="group bg-white rounded-2xl border border-background-200 p-6 md:p-8 hover:border-accent-300 hover:shadow-lg transition-all duration-300 h-full">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center shrink-0 group-hover:bg-primary-200 transition-colors duration-300">
                        <i className={`${item.icon} text-xl text-primary-700`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-foreground-900">{item.title}</h3>
                        <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                </SectionReveal>
              ))}
            </div>

            <SectionReveal className="mt-8 md:mt-10">
              <div className="bg-primary-800 rounded-2xl overflow-hidden p-6 md:p-8 flex flex-col md:flex-row items-center gap-5 md:gap-8">
                <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                  <i className="ri-stethoscope-line text-3xl text-white" />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-white">跨科整合會診服務</h3>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    共聽生活協會與內分泌科、心臟科、神經科及精神科合作，提供聽損者一站式跨科會診，
                    讓慢性病與聽力問題同步管理，減少重複就醫的負擔。
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/booking')}
                  className="bg-white text-primary-800 px-6 py-3 rounded-xl text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap flex items-center gap-2 shrink-0"
                >
                  <i className="ri-calendar-check-line" />
                  預約會診
                </button>
              </div>
            </SectionReveal>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 聲音平權 CSR =====
            ============================================================ */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="CSR & Sound Equity" title="聲音平權領引活動" subtitle="以企業社會責任為核心，推動聽損平權、教育與社區共融" />

          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-background-200 -translate-x-1/2" />

            <div className="space-y-6 md:space-y-8">
              {csrActivities.map((item, idx) => (
                <SectionReveal key={item.title} delay={idx % 2}>
                  <div className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`md:w-1/2 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="group inline-block text-left w-full md:w-auto">
                        <div className="relative bg-white rounded-xl border border-background-200 p-5 md:p-6 hover:border-accent-300 hover:shadow-sm transition-all duration-300 overflow-hidden">
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-accent-500 rounded-l-xl" />
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold bg-primary-700 text-background-50 px-2.5 py-1 rounded-full">
                              <i className="ri-calendar-line text-xs" />
                              {item.year}
                            </span>
                          </div>
                          <h3 className="text-base font-bold text-foreground-900">{item.title}</h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    </div>

                    <div className="relative shrink-0 z-10 hidden md:flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-700 border-[3px] border-white shadow-sm flex items-center justify-center">
                        <i className={`${item.icon} text-white text-sm`} />
                      </div>
                    </div>

                    <div className="md:w-1/2" />
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 亞洲共聽社群 — 大幅升級 =====
            ============================================================ */}
        <div className="bg-background-100 py-10 md:py-16">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionHeader label="Asia Co-Hearing Community" title="亞洲共聽社群" subtitle="連結亞洲 8 國聽力組織，建立區域合作網絡，共享資源與經驗" />

            {/* 地圖 + 國家卡片 整合區塊 */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
              {/* 左側：亞洲聯盟網絡 — 行銷導向，凸顯網絡實力 */}
              <SectionReveal className="lg:col-span-5">
                <div className="relative bg-primary-800 rounded-2xl overflow-hidden h-80 lg:h-full min-h-[340px] p-6 md:p-8 flex flex-col justify-between group">
                  {/* 光暈裝飾 */}
                  <div className="absolute -top-20 -right-16 w-60 h-60 rounded-full bg-accent-400/10 blur-3xl" />
                  <div className="absolute top-1/2 -left-20 w-48 h-48 rounded-full bg-primary-400/6 blur-3xl" />
                  <div className="absolute -bottom-12 right-0 w-44 h-44 rounded-full bg-accent-300/5 blur-3xl" />

                  {/* 網格紋理 */}
                  <div className="absolute inset-0 opacity-[0.025]">
                    <svg width="100%" height="100%">
                      <defs>
                        <pattern id="network-grid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse">
                          <circle cx="16" cy="16" r="0.5" fill="white" />
                        </pattern>
                      </defs>
                      <rect x="0" y="0" width="100%" height="100%" fill="url(#network-grid)" />
                    </svg>
                  </div>

                  {/* 節點網絡視覺 — 8 國圍繞核心的放射狀網絡 */}
                  <div className="absolute inset-0 opacity-[0.2]">
                    <svg viewBox="0 0 400 340" className="w-full h-full" fill="none">
                      <defs>
                        <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="white" stopOpacity="0.6" />
                          <stop offset="30%" stopColor="white" stopOpacity="0.25" />
                          <stop offset="60%" stopColor="white" stopOpacity="0.06" />
                          <stop offset="100%" stopColor="white" stopOpacity="0" />
                        </radialGradient>
                        <radialGradient id="nodeGlowSm" cx="50%" cy="50%" r="50%">
                          <stop offset="0%" stopColor="white" stopOpacity="0.8" />
                          <stop offset="50%" stopColor="white" stopOpacity="0.35" />
                          <stop offset="100%" stopColor="white" stopOpacity="0" />
                        </radialGradient>
                      </defs>

                      {/* 8 個國家節點排列在橢圓軌道上 */}
                      {[
                        { name: '台灣', cx: 200, cy: 40, r: 4.5 },
                        { name: '日本', cx: 320, cy: 62, r: 4 },
                        { name: '韓國', cx: 360, cy: 140, r: 3.8 },
                        { name: '新加坡', cx: 355, cy: 230, r: 3.8 },
                        { name: '馬來西亞', cx: 300, cy: 300, r: 3.5 },
                        { name: '泰國', cx: 200, cy: 320, r: 3.5 },
                        { name: '越南', cx: 100, cy: 300, r: 3.5 },
                        { name: '菲律賓', cx: 45, cy: 230, r: 3.8 },
                      ].map((n, i) => (
                        <g key={`net-node-${i}`}>
                          {/* 到核心的連線 */}
                          <line x1={n.cx} y1={n.cy} x2="200" y2="165" stroke="white" strokeWidth={i === 0 ? 1.2 : 0.7} strokeOpacity={0.25 + (i % 3) * 0.04} strokeDasharray={i === 0 ? 'none' : '4 5'} />
                          {/* 節點光暈 */}
                          <circle cx={n.cx} cy={n.cy} r={9} fill="url(#nodeGlowSm)" />
                          {/* 節點本體 */}
                          <circle cx={n.cx} cy={n.cy} r={n.r} fill="white" stroke="white" strokeWidth="0.8" opacity={i === 0 ? 0.85 : 0.55 + (i % 2) * 0.08} />
                        </g>
                      ))}

                      {/* 節點間橫向連線 — 形成網狀 */}
                      {[
                        [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 0],
                        [1, 3], [2, 5], [4, 6], [5, 7], [0, 3], [1, 4],
                      ].map((pair, i) => {
                        const nodes = [
                          { cx: 200, cy: 40 }, { cx: 320, cy: 62 }, { cx: 360, cy: 140 }, { cx: 355, cy: 230 },
                          { cx: 300, cy: 300 }, { cx: 200, cy: 320 }, { cx: 100, cy: 300 }, { cx: 45, cy: 230 },
                        ];
                        return (
                          <line
                            key={`inter-${i}`}
                            x1={nodes[pair[0]].cx} y1={nodes[pair[0]].cy}
                            x2={nodes[pair[1]].cx} y2={nodes[pair[1]].cy}
                            stroke="white" strokeWidth="0.4" strokeOpacity="0.12" strokeDasharray="2 6"
                          />
                        );
                      })}

                      {/* 核心 Hub — 協會標誌點 */}
                      <circle cx="200" cy="165" r="28" fill="url(#hubGlow)" />
                      <circle cx="200" cy="165" r="10" fill="white" stroke="white" strokeWidth="1.5" opacity="0.7" />
                      <circle cx="200" cy="165" r="5" fill="white" opacity="0.9" />

                      {/* 發散波紋 */}
                      <circle cx="200" cy="165" r="40" stroke="white" strokeWidth="0.6" strokeOpacity="0.08" strokeDasharray="3 6" />
                      <circle cx="200" cy="165" r="55" stroke="white" strokeWidth="0.4" strokeOpacity="0.06" strokeDasharray="2 8" />
                    </svg>
                  </div>

                  {/* 上方資訊 */}
                  <div className="relative z-10">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/12 flex items-center justify-center backdrop-blur-sm border border-white/10">
                        <i className="ri-global-line text-2xl text-white" />
                      </div>
                      <div>
                        <span className="text-xs font-semibold text-accent-400 tracking-[0.2em] uppercase">Asia Co-Hearing Network</span>
                        <h3 className="text-xl font-bold text-white leading-tight">亞洲聯盟網絡</h3>
                      </div>
                    </div>

                    {/* 核心價值主張 */}
                    <div className="pl-[60px]">
                      <p className="text-sm text-white/85 leading-relaxed font-medium">
                        連結<span className="text-accent-400 font-bold"> 8 國</span>聽力組織，
                        <span className="text-accent-400 font-bold">12,000+</span> 會員共建
                        <br />亞洲最大聽力健康生態系
                      </p>

                      {/* 8 國標籤雲 */}
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {[
                          { n: '台灣', accent: true }, { n: '日本' }, { n: '韓國' }, { n: '新加坡' },
                          { n: '馬來西亞' }, { n: '泰國' }, { n: '越南' }, { n: '菲律賓' },
                        ].map((c) => (
                          <span
                            key={c.n}
                            className={`px-2.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              c.accent
                                ? 'bg-accent-500/20 text-accent-300 border-accent-500/30'
                                : 'bg-white/8 text-white/70 border-white/10'
                            }`}
                          >
                            {c.n}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* 底部：三大核心數據 + CTA */}
                  <div className="relative z-10">
                    <div className="grid grid-cols-3 gap-2.5 mb-3">
                      <div className="bg-white/6 rounded-lg p-2.5 backdrop-blur-sm border border-white/5 hover:bg-white/10 transition-colors duration-300 text-center">
                        <div className="text-lg font-bold text-white">8<span className="text-xs text-accent-400 ml-0.5">國</span></div>
                        <div className="mt-0.5 text-xs text-white/50 font-medium uppercase">合作國家</div>
                      </div>
                      <div className="bg-white/6 rounded-lg p-2.5 backdrop-blur-sm border border-white/5 hover:bg-white/10 transition-colors duration-300 text-center">
                        <div className="text-lg font-bold text-white">50<span className="text-xs text-accent-400 ml-0.5">+</span></div>
                        <div className="mt-0.5 text-xs text-white/50 font-medium uppercase">跨境計畫</div>
                      </div>
                      <div className="bg-white/6 rounded-lg p-2.5 backdrop-blur-sm border border-white/5 hover:bg-white/10 transition-colors duration-300 text-center">
                        <div className="text-lg font-bold text-white">12k<span className="text-xs text-accent-400 ml-0.5">+</span></div>
                        <div className="mt-0.5 text-xs text-white/50 font-medium uppercase">總會員數</div>
                      </div>
                    </div>

                    {/* CTA */}
                    <button
                      type="button"
                      onClick={() => navigate('/contact')}
                      className="w-full flex items-center justify-center gap-2 bg-white/12 hover:bg-white/18 text-white border border-white/15 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 backdrop-blur-sm"
                    >
                      <i className="ri-shake-hands-line" />
                      加入亞洲聯盟網絡
                    </button>
                  </div>

                  {/* 底部微光條 */}
                  <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
                </div>
              </SectionReveal>

              {/* 右側：國家卡片 */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {asiaCountries.map((country, idx) => (
                  <SectionReveal key={country.name} delay={idx}>
                    <div className="group bg-white rounded-xl border border-background-200 p-5 md:p-6 hover:border-accent-300 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 h-full relative overflow-hidden">
                      {/* 頂部漸層裝飾線 */}
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-accent-400 via-primary-400 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* 裝飾背景光點 */}
                      <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full bg-accent-100/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg" />

                      <div className="relative flex items-start gap-4">
                        {/* 圖標 - 有層次的雙層設計 */}
                        <div className="relative shrink-0">
                          <div className="w-11 h-11 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 group-hover:scale-110 transition-all duration-300">
                            <i className={`${country.flag} text-lg text-primary-700`} />
                          </div>
                          {/* 節點連結點 */}
                          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-accent-400 border-2 border-white opacity-0 group-hover:opacity-100 transition-all duration-300" />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-sm font-bold text-foreground-900 whitespace-nowrap">{country.name}</h3>
                            <span className="text-xs font-semibold bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full whitespace-nowrap">{country.role}</span>
                          </div>
                          <p className="text-sm text-foreground-600 leading-relaxed">{country.desc}</p>

                          {/* 底部裝飾：細微進度條 */}
                          <div className="mt-3 flex items-center gap-2">
                            <div className="flex-1 h-0.5 bg-background-200 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-accent-400 rounded-full transition-all duration-500 group-hover:w-full"
                                style={{ width: `${30 + idx * 10}%` }}
                              />
                            </div>
                            <span className="text-[10px] text-foreground-400 font-medium uppercase whitespace-nowrap">active</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SectionReveal>
                ))}
              </div>
            </div>

            {/* 亞洲論壇橫幅 */}
            <SectionReveal className="mt-8 md:mt-10">
              <div className="rounded-2xl overflow-hidden border border-background-200 h-56 md:h-72 relative">
                <img
                  src="https://readdy.ai/api/search-image?query=asia%20pacific%20map%20with%20connected%20network%20dots%20and%20lines%20representing%20collaboration%20warm%20green%20tones%20abstract%20modern%20infographic%20style%20professional%20clean%20background&width=1200&height=500&seq=80&orientation=landscape"
                  alt="亞洲共聽社群網絡"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-primary-900/50 flex items-center justify-center">
                  <div className="text-center text-white px-6">
                    <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-global-line text-2xl text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold">亞洲聽力健康論壇</h3>
                    <p className="mt-2 text-sm text-white/80 max-w-lg mx-auto">
                      每年舉辦一次，邀請各國聽力專家、政策制定者與民間組織共同參與，推動區域聽力保健政策與資源整合。
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/contact')}
                      className="mt-4 inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white border border-white/30 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 backdrop-blur-sm"
                    >
                      <i className="ri-calendar-event-line" />
                      了解下一屆論壇
                    </button>
                  </div>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L48 55C96 50 192 40 288 36C384 32 480 32 576 34C672 36 768 40 864 44C960 48 1056 52 1152 50C1248 48 1344 40 1392 36L1440 32V0H1392C1344 0 1248 0 1152 0C1056 0 960 0 864 0C768 0 672 0 576 0C480 0 384 0 288 0C192 0 96 0 48 0H0V60Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== 亞洲聽力健康峰會 =====
            ============================================================ */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-16 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-primary-800 rounded-2xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-64 h-64 md:w-80 md:h-80 rounded-full bg-accent-400/10 blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 rounded-full bg-primary-400/10 blur-2xl" />

              <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 p-6 md:p-10 lg:p-12">
                {/* 左側文字 */}
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                      <i className="ri-award-line text-white" />
                    </div>
                    <span className="text-xs font-semibold text-accent-400 tracking-wider uppercase">Asia Hearing Health Summit</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight">
                    亞洲聽力健康峰會
                  </h2>
                  <p className="mt-3 text-white/70 text-sm md:text-base leading-relaxed">
                    亞洲聽力健康領域最具影響力的年度盛會，匯集 8 國頂尖專家、政策制定者與產業領袖，共同推動區域聽力保健與聽力平權。
                  </p>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/80 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                      <i className="ri-calendar-line text-accent-400" /> 2026.10.15 – 17
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/80 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                      <i className="ri-map-pin-line text-accent-400" /> 台北・國際會議中心
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs text-white/80 bg-white/10 px-3 py-1.5 rounded-full border border-white/10">
                      <i className="ri-user-star-line text-accent-400" /> 50+ 國際講者
                    </span>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate('/asia-summit')}
                      className="bg-white text-primary-800 px-6 py-3 rounded-xl text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-article-line" />
                      查看峰會詳情
                    </button>
                    {/* <button
                      type="button"
                      onClick={() => navigate('/contact')}
                      className="border border-white/30 text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors whitespace-nowrap flex items-center justify-center gap-2"
                    >
                      <i className="ri-calendar-check-line" />
                      立即報名
                    </button> */}
                  </div>
                </div>

                {/* 右側圖片 */}
                <div className="relative h-56 md:h-auto rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=international%20conference%20summit%20stage%20with%20diverse%20asian%20audience%20and%20speakers%20professional%20modern%20auditorium%20warm%20lighting%20green%20and%20orange%20tones%20professional%20event%20photography%20high%20quality%20wide%20angle&width=700&height=500&seq=assoc-summit-1&orientation=landscape"
                    alt="亞洲聽力健康峰會"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-primary-900/20" />
                </div>
              </div>
            </div>
          </SectionReveal>
        </div>

        {/* Wave */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="background-100" />
          </svg>
        </div>

        {/* ============================================================
            ===== CTA =====
            ============================================================ */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionReveal>
            <div className="bg-primary-800 rounded-2xl overflow-hidden p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8 relative">
              <div className="absolute top-0 right-0 w-40 h-40 md:w-60 md:h-60 rounded-full bg-accent-400/10 blur-2xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 md:w-48 md:h-48 rounded-full bg-primary-400/10 blur-2xl" />
              <div className="relative flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                    <i className="ri-group-line text-white" />
                  </div>
                  <span className="text-xs font-semibold text-accent-400 tracking-wider uppercase">加入我們</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white">
                  成為共聽生活協會的一份子
                </h3>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                  無論您是聽損者、家屬、醫療專業人士或企業夥伴，都歡迎加入我們，一起為聽力平權與健康生活努力。從台灣到全亞洲，我們需要您的力量。
                </p>
              </div>
              <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="bg-white text-primary-800 px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-background-100 transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <i className="ri-mail-line" />
                  聯繫協會
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/booking')}
                  className="border border-white/30 text-white px-6 md:px-8 py-3 rounded-xl text-sm font-semibold hover:bg-white/10 transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                >
                  <i className="ri-calendar-check-line" />
                  預約諮詢
                </button>
              </div>
            </div>
          </SectionReveal>
        </div>
      </main>

      <Footer />
    </div>
  );
}